import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import Button from '../ui/Button';

// Turn Firebase auth error codes into messages that actually point at the cause,
// instead of always blaming a disabled provider.
const messageForError = (err) => {
    switch (err?.code) {
        case 'auth/unauthorized-domain':
            return 'This site’s domain isn’t authorized for sign-in. Add it under Firebase → Authentication → Settings → Authorized domains.';
        case 'auth/operation-not-allowed':
            return 'Google sign-in isn’t enabled for this project. Enable it under Firebase → Authentication → Sign-in method.';
        case 'auth/popup-blocked':
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
            return 'The sign-in window was blocked or closed. Please allow popups and try again.';
        case 'auth/network-request-failed':
            return 'Network error during sign-in. Check your connection and try again.';
        default:
            return 'Google sign-in failed. Please try again.';
    }
};

const Login = () => {
    const { user, signInWithGoogle, continueAsGuest } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Preserve the query string too, so a redirect from the editor (/edit?src=…)
    // returns the user to that exact image, not just the bare /edit route.
    const from =
        (location.state?.from?.pathname || '/photos') + (location.state?.from?.search || '');
    const isFromEditor = location.state?.from?.pathname?.startsWith('/edit');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    // The mobile redirect flow reloads the app back onto this page, so navigate
    // away once the user resolves (location.state is lost across the reload, so
    // this falls back to /photos in that case).
    useEffect(() => {
        if (user) navigate(from, { replace: true });
    }, [user, from, navigate]);

    const handleGoogle = async () => {
        setError('');
        setBusy(true);
        try {
            // On mobile this redirects away and never returns here; the effect
            // above handles navigation after the redirect completes.
            const signedInUser = await signInWithGoogle();
            if (signedInUser) navigate(from, { replace: true });
        } catch (err) {
            console.error(err);
            setError(messageForError(err));
        } finally {
            setBusy(false);
        }
    };

    const handleGuest = () => {
        continueAsGuest();
        // Editing requires an account. Guests cannot access /edit, so redirect to /photos.
        const target = isFromEditor ? '/photos' : from;
        navigate(target, { replace: true });
    };

    return (
        <div className="flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12" style={{ minHeight: 'calc(100vh - 4rem)' }}>
            <div className="w-full max-w-md bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl shadow-e0 p-8 text-center">
                <span className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-brand-400 to-brand-600 bg-clip-text text-transparent">
                    Pixiy
                </span>
                <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white mt-4 mb-1">
                    {isFromEditor ? 'Sign in to edit photos' : 'Save your favorites'}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mb-8">
                    {isFromEditor
                        ? 'Image editing requires an account. Sign in with Google to crop, annotate, and export.'
                        : 'Browsing and downloads are always free. Sign in to keep your favorites synced to your account.'}
                </p>

                {error &&
                    <p className="mb-4 text-sm text-rose-500 bg-rose-50 dark:bg-rose-900 dark:bg-opacity-20 rounded-lg px-3 py-2">
                        {error}
                    </p>
                }

                <Button
                    variant="outline"
                    size="lg"
                    onClick={handleGoogle}
                    disabled={busy}
                    className="w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 dark:text-white disabled:cursor-wait"
                >
                    <FontAwesomeIcon icon={faGoogle} style={{ color: '#EA4335' }} />
                    {busy ? 'Connecting…' : 'Continue with Google'}
                </Button>

                <div className="flex items-center gap-3 my-6">
                    <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                    <span className="text-xs uppercase tracking-wide text-gray-400">or</span>
                    <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                </div>

                <Button variant="ghost" size="lg" onClick={handleGuest} className="w-full">
                    Continue without an account
                    <FontAwesomeIcon icon={faArrowRight} />
                </Button>

                <p className="mt-6 text-xs text-gray-400">
                    {isFromEditor
                        ? 'Browsing and downloads are always free without an account.'
                        : 'No account needed to browse and download — signing in just lets us remember you.'}
                </p>
            </div>
        </div>
    );
};

export default Login;
